/**
 * Custom hook wrapper around react-speech-recognition package
 *
 * This implementation uses the react-speech-recognition npm package
 * instead of the native Web Speech API directly.
 *
 * Benefits:
 * - Better cross-browser handling
 * - Built-in auto-restart mechanism
 * - Simplified API
 * - Community maintained
 *
 * CRITICAL FIX: This package has a global singleton state which causes
 * transcript leakage across multiple inputs. We implement an activeInputId
 * system to ensure only ONE input receives transcripts at a time.
 */

import { useEffect, useCallback, useRef, useState } from "react";
import SpeechRecognition, { useSpeechRecognition as usePackageSpeechRecognition } from "react-speech-recognition";

interface UseSpeechRecognitionOptions {
  continuous?: boolean;
  interimResults?: boolean;
  language?: string;
  inputId: string; // REQUIRED: Unique identifier for this input
  onResult?: (transcript: string, isFinal: boolean) => void;
  onError?: (error: any) => void;
}

interface UseSpeechRecognitionReturn {
  isListening: boolean;
  isActivelyListening: boolean; // True only when THIS input is actively listening
  isSupported: boolean;
  error: string | null;
  transcript: string;
  startListening: () => void;
  stopListening: () => void;
  resetTranscript: () => void;
  resetError: () => void;
  syncTranscriptPosition: () => void;
}

// Global singleton to track which input is currently active
// This prevents transcript leakage across multiple inputs on the same page
let activeInputId: string | null = null;
const subscribers = new Set<(id: string | null) => void>();

function setActiveInputId(id: string | null) {
  activeInputId = id;
  subscribers.forEach((fn) => fn(id));
}

const useSpeechRecognition = (options: UseSpeechRecognitionOptions): UseSpeechRecognitionReturn => {
  const { continuous = false, interimResults = false, language = "en-US", inputId, onResult, onError } = options;

  // Use the package hook
  const { transcript, interimTranscript, finalTranscript, resetTranscript, browserSupportsSpeechRecognition } =
    usePackageSpeechRecognition();

  // Local reactive mirror of the module-level activeInputId.
  // Initialized from the current module value so newly-mounted inputs
  // immediately reflect any already-active session.
  const [reactiveActiveId, setReactiveActiveId] = useState<string | null>(activeInputId);

  // Subscribe to activeInputId changes for the lifetime of this hook instance.
  useEffect(() => {
    subscribers.add(setReactiveActiveId);
    // Sync in case the value changed between render and effect execution.
    setReactiveActiveId(activeInputId);
    return () => {
      subscribers.delete(setReactiveActiveId);
    };
  }, []);

  const prevFinalTranscriptRef = useRef("");
  const prevInterimTranscriptRef = useRef("");

  const [error, setError] = useState<string | null>(null);

  const isSupported = browserSupportsSpeechRecognition;

  // Handle transcript changes - only for the active input
  useEffect(() => {
    if (!onResult) return;
    if (activeInputId !== inputId) return;
    if (finalTranscript && finalTranscript !== prevFinalTranscriptRef.current) {
      const newPortion = finalTranscript.substring(prevFinalTranscriptRef.current.length).trim();
      if (newPortion) {
        onResult(newPortion, true);
      }
      prevFinalTranscriptRef.current = finalTranscript;
    }

    if (interimResults && interimTranscript && interimTranscript !== prevInterimTranscriptRef.current) {
      onResult(interimTranscript, false);
      prevInterimTranscriptRef.current = interimTranscript;
    }
  }, [finalTranscript, interimTranscript, interimResults, onResult, inputId]);

  const startListening = useCallback(() => {
    if (!isSupported) {
      const errorMsg = "Speech recognition is not supported in this browser.";
      setError(errorMsg);
      if (onError) {
        onError(new Error(errorMsg));
      }
      return;
    }

    // Mark this input as active before starting so UI updates synchronously
    // and transcript routing is correct from the first result.
    setActiveInputId(inputId);

    setError(null);
    prevFinalTranscriptRef.current = "";
    prevInterimTranscriptRef.current = "";

    resetTranscript();
    SpeechRecognition.startListening({ continuous, language })
      .then(() => {})
      .catch((err: Error) => {
        // Clear tracking state on failure so the mic is not stuck.
        setActiveInputId(null);
        const errorMsg = err.message || "Failed to start speech recognition.";
        setError(errorMsg);
        if (onError) {
          onError(err);
        }
      });
  }, [isSupported, continuous, language, onError, resetTranscript, inputId]);

  const stopListening = useCallback(() => {
    if (activeInputId === inputId) {
      setActiveInputId(null);

      const recognition = SpeechRecognition.getRecognition();
      if (recognition) {
        recognition.continuous = false;
      }

      SpeechRecognition.stopListening();

      setTimeout(() => {
        resetTranscript();
        prevFinalTranscriptRef.current = "";
        prevInterimTranscriptRef.current = "";
      }, 100);
    }
  }, [resetTranscript, inputId]);

  const resetError = useCallback(() => {
    setError(null);
  }, []);

  const wrappedResetTranscript = useCallback(() => {
    resetTranscript();
    prevFinalTranscriptRef.current = "";
    prevInterimTranscriptRef.current = "";
  }, [resetTranscript]);
  const syncTranscriptPosition = useCallback(() => {
    prevFinalTranscriptRef.current = finalTranscript;
    prevInterimTranscriptRef.current = interimTranscript;
  }, [finalTranscript, interimTranscript]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (activeInputId === inputId) {
        setActiveInputId(null);
      }
      const recognition = SpeechRecognition.getRecognition();
      if (recognition) {
        recognition.continuous = false;
      }
      SpeechRecognition.stopListening();
    };
  }, [inputId]);

  return {
    // Derived from our own tracking, not the package's `listening` state.
    isListening: reactiveActiveId !== null,
    isActivelyListening: reactiveActiveId === inputId,
    isSupported,
    error,
    transcript,
    startListening,
    stopListening,
    resetTranscript: wrappedResetTranscript,
    resetError,
    syncTranscriptPosition,
  };
};

export default useSpeechRecognition;
